import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./ZleceniaStyle/FormularzZlecenia.css";
import KosztySprzedazTabela from "./KosztySprzedazTabela";
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import SignatureCanvas from 'react-signature-canvas';
import logoImg from '../../LogoG3.png';
import { hasPermission } from '../../auth/permissionUtils';

const API_URL = process.env.REACT_APP_API_URL;
pdfMake.vfs = pdfFonts.default.vfs;

/* ================== POMOCNICZE ================== */
function formatDateForInput(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  const tzOffset = date.getTimezoneOffset() * 60000;
  const localDate = new Date(date - tzOffset);
  return localDate.toISOString().slice(0, 16);
}

function formatAddressLine(adres) {
  if (!adres) return "Brak adresu";
  const nrDomu = adres.nr_domu != null ? String(adres.nr_domu).trim() : "";
  const nrLokalu = adres.nr_lokalu != null ? String(adres.nr_lokalu).trim() : "";
  const numerBudynku = nrDomu && nrLokalu ? `${nrDomu}/${nrLokalu}` : (nrDomu || nrLokalu);

  return [adres.miasto, adres.kod_pocztowy, adres.ulica, numerBudynku]
    .filter((part) => part !== null && part !== undefined && String(part).trim() !== "")
    .join(" ");
}

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const toNum = (v) => {
  if (v === "" || v == null) return NaN;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
};
const isQtyValid = (q) => {
  const n = toNum(q);
  return Number.isFinite(n) && n > 0;
};

function computeAutoStatus(formData) {
  if (!formData.pracownicy || formData.pracownicy.length === 0) return "Nowe";
  let autoStatus = "W realizacji";
  const opisFilled = formData.opis_prac && formData.opis_prac.trim() !== "";
  const hasDataOd = formData.data_realizacji_od && formData.data_realizacji_od.trim() !== "";
  const hasDataDo = formData.data_realizacji_do && formData.data_realizacji_do.trim() !== "";
  if (opisFilled && hasDataOd && hasDataDo) autoStatus = "Zrealizowane";
  return autoStatus;
}

function canBeZrealizowane(formData) {
  if (!formData.pracownicy || formData.pracownicy.length === 0) return false;
  if (!formData.opis_prac || !formData.opis_prac.trim()) return false;
  if (!formData.data_realizacji_od || !formData.data_realizacji_od.trim()) return false;
  if (!formData.data_realizacji_do || !formData.data_realizacji_do.trim()) return false;
  return true;
}

function isFieldDisabled(fieldName, status) {
  const isZrealizowane = status === "Zrealizowane";
  const isRozliczone = status === "Rozliczone";

  if (isRozliczone) {
    const allowedFields = ["status", "cena", "koszt_praca", "koszt_materialy", "koszt_urzadzenia", "koszt_dojazd"];
    return !allowedFields.includes(fieldName);
  }
  if (isZrealizowane) {
    const lockedFields = ["planowana_data_realizacji", "opis", "rodzaj_zlecenia"];
    return lockedFields.includes(fieldName);
  }
  return false;
}

/* ================== KOMPONENT ================== */
function FormularzEdycjiZlecenia({ uzytkownik, zlecenieId, onZamknij, onKontynuacja, towary, slowniki }) {
  const canEditAllOrders = hasPermission(uzytkownik, 'orders_edit_all');
  const canContinueOrder = hasPermission(uzytkownik, 'orders_continue');
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);
  
  // Stany do wizardu PDF i podpisu
  const [step, setStep] = useState('form'); // 'form' | 'preview' | 'signature'
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const sigCanvas = useRef({});
  
  // STAN LOKALNY TYLKO DLA PDF - Imię i nazwisko odbiorcy prac
  const [odbiorcaPrac, setOdbiorcaPrac] = useState("");

  const [formData, setFormData] = useState({
    rodzaj_zlecenia: "Serwis", planowana_data_realizacji: "", status: "Nowe", opis: "",
    cena: 0, koszt_praca: 0, koszt_materialy: 0, koszt_urzadzenia: 0, koszt_dojazd: 0,
    rabat_specjalny: 0, opiekun_zlecenia: "", pracownicy: [], id_adres: null,
    opis_prac: "", data_realizacji_od: "", data_realizacji_do: "",
  });

  const [costRows, setCostRows] = useState([]);
  const [initialCostRows, setInitialCostRows] = useState([]);
  const initialCostIdsRef = useRef([]);

  const mapDbCostToUiRow = (k, idx) => {
    const qty = toNum(k.ilosc);
    const cn = toNum(k.koszt_jednostkowy_netto_at_sale);
    const vat = toNum(k.vat_proc_at_sale);

    const kwota_vat = Number.isFinite(cn) && Number.isFinite(vat) ? round2(cn * (vat / 100)) : "";
    const razem_netto = Number.isFinite(cn) && Number.isFinite(qty) ? round2(cn * qty) : "";
    const razem_vat = Number.isFinite(qty) && kwota_vat !== "" ? round2(kwota_vat * qty) : "";
    const razem_brutto = razem_netto !== "" && Number.isFinite(vat) ? round2(razem_netto * (1 + vat / 100)) : "";

    return {
      id: idx + 1, isEditing: false, id_koszt: k.id_koszt ?? k.id ?? null, id_towar: k.id_towar ?? null,
      kategoria_id: k.kategoria_id ?? null, jednostka_id: k.jednostka_id ?? null, vat_id: k.vat_id ?? null,
      nazwa_at_sale: k.nazwa_at_sale || "", kategoria_nazwa_at_sale: k.kategoria_nazwa_at_sale || "",
      jednostka_symbol_at_sale: k.jednostka_symbol_at_sale || "", ilosc: k.ilosc ?? "",
      koszt_jednostkowy_netto_at_sale: k.koszt_jednostkowy_netto_at_sale ?? "", marza_at_sale: k.marza_at_sale ?? "",
      vat_nazwa_at_sale: k.vat_nazwa_at_sale || "", vat_proc_at_sale: k.vat_proc_at_sale ?? "",
      in_sprzedaz: k.in_sprzedaz ?? true, kwota_vat, razem_vat, razem_netto, razem_brutto,
    };
  };

  const [selectedUrzadzenia, setSelectedUrzadzenia] = useState([]);
  const [availableUrzadzenia, setAvailableUrzadzenia] = useState([]);
  const [pracownicy, setPracownicy] = useState([]);
  const [clientInfo, setClientInfo] = useState(null);
  const [adresInfo, setAdresInfo] = useState(null);
  const [orderFiles, setOrderFiles] = useState([]);
  const [existingOrderFiles, setExistingOrderFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [previewMap, setPreviewMap] = useState({});
  const fileInputRef = useRef(null);

  const id_zlecenia = zlecenieId;

  useEffect(() => {
    if (loading) return;
    const fetchFiles = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_URL}/pliki_zlecenia/zlecenia?id_zlecenia=${zlecenieId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setExistingOrderFiles(res.data);
      } catch (err) {
        console.error("Błąd plików:", err);
      }
    };
    fetchFiles();
  }, [loading, zlecenieId]);

  const handleChooseFilesClick = () => fileInputRef.current?.click();

  useEffect(() => {
    if (!existingOrderFiles.length) return;
    const token = localStorage.getItem("token");
    existingOrderFiles.forEach(async (f) => {
      if (previewMap[f.id_pliku]) return;
      try {
        const res = await axios.get(`${API_URL}/pliki_zlecenia/${f.id_pliku}`, {
          responseType: "blob", headers: { Authorization: `Bearer ${token}` },
        });
        const blobUrl = URL.createObjectURL(res.data);
        setPreviewMap((prev) => ({ ...prev, [f.id_pliku]: blobUrl }));
      } catch (err) {}
    });
    return () => Object.values(previewMap).forEach(url => URL.revokeObjectURL(url));
  }, [existingOrderFiles]);

  const handleFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setOrderFiles((prev) => [...prev, ...files].filter((f, i, arr) => 
      arr.findIndex((x) => x.name === f.name && x.size === f.size) === i
    ));
  };

  const handleRemoveFile = (idx) => setOrderFiles(prev => prev.filter((_, i) => i !== idx));

  const handleUploadFiles = async () => {
    if (!orderFiles.length) return;
    setUploadingFiles(true);
    try {
      const token = localStorage.getItem("token");
      await Promise.all(orderFiles.map(file => {
        const fd = new FormData();
        fd.append("plik", file);
        fd.append("nazwa_pliku", file.name);
        fd.append("idPowiazania", id_zlecenia);
        return axios.post(`${API_URL}/pliki_zlecenia/upload/zlecenie`, fd, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }));
      setOrderFiles([]);
      const res = await axios.get(`${API_URL}/pliki_zlecenia/zlecenia?id_zlecenia=${zlecenieId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExistingOrderFiles(res.data);
    } catch (err) {
      console.error("Błąd uploadu:", err);
      throw err;
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleDeleteExistingFile = async (fileId) => {
    if (!window.confirm("Na pewno usunąć ten plik?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/pliki_zlecenia/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExistingOrderFiles(prev => prev.filter(f => f.id_pliku !== fileId));
    } catch (err) {
      console.error("Błąd usuwania:", err);
    }
  };

  const handleDownloadExistingFile = async (fileId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/pliki_zlecenia/${fileId}`, {
        responseType: "blob", headers: { Authorization: `Bearer ${token}` },
      });
      let fileName = "plik";
      const cd = res.headers["content-disposition"];
      if (cd && cd.includes("filename=")) {
        fileName = decodeURIComponent(cd.split("filename=")[1].replace(/["']/g, ""));
      }
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Błąd pobierania:", err);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/zlecenia/${id_zlecenia}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data;
        setOrderData(data);
        setFormData({
          rodzaj_zlecenia: data.rodzaj_zlecenia, planowana_data_realizacji: data.planowana_data_realizacji ? formatDateForInput(data.planowana_data_realizacji) : "",
          status: data.status, opis: data.opis || "", cena: data.cena, koszt_praca: data.koszt_praca, koszt_materialy: data.koszt_materialy,
          koszt_urzadzenia: data.koszt_urzadzenia, koszt_dojazd: data.koszt_dojazd, rabat_specjalny: data.rabat_specjalny || 0,
          opiekun_zlecenia: data.opiekun_zlecenia, pracownicy: data.pracownicy ? data.pracownicy.map(p => p.id) : [],
          data_realizacji_od: data.data_realizacji_od ? formatDateForInput(data.data_realizacji_od) : "",
          data_realizacji_do: data.data_realizacji_do ? formatDateForInput(data.data_realizacji_do) : "",
          id_adres: data.id_adres, opis_prac: data.opis_prac || "",
        });

        setSelectedUrzadzenia((data.urzadzenia || []).map(dev => ({
          id_urzadzenie: dev.id, nazwa: dev.nazwa, model: dev.model, numer_seryjny: dev.numer_seryjny,
        })));

        const [resDevices, resWorkers, resAddr] = await Promise.all([
          axios.get(`${API_URL}/urzadzenia?id_adres=${data.id_adres}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/pracownicy`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/adresy/${data.id_adres}`, { headers: { Authorization: `Bearer ${token}` } })
        ]);

        setAvailableUrzadzenia(resDevices.data);
        setPracownicy(resWorkers.data.filter(p => p.id_pracownika !== null));
        setAdresInfo(resAddr.data);

        if (resAddr.data.id_klient) {
          const resClient = await axios.get(`${API_URL}/klienci/${resAddr.data.id_klient}`, { headers: { Authorization: `Bearer ${token}` } });
          setClientInfo(resClient.data);
        }

        const resCosts = await axios.get(`${API_URL}/koszty-pozycje/zlecenia/${id_zlecenia}`, { headers: { Authorization: `Bearer ${token}` } });
        const dbCosts = Array.isArray(resCosts.data) ? resCosts.data : [];
        initialCostIdsRef.current = dbCosts.map(r => r.id_koszt || r.id).filter(Boolean);
        setInitialCostRows(dbCosts.map(mapDbCostToUiRow));

        setLoading(false);
      } catch (err) {
        console.error("Błąd ładowania:", err);
        setLoading(false);
      }
    })();
  }, [id_zlecenia]);

  useEffect(() => {
    if (formData.status !== "Rozliczone") {
      const auto = computeAutoStatus(formData);
      setFormData(prev => ({ ...prev, status: auto }));
    }
  }, [formData.pracownicy, formData.opis_prac, formData.data_realizacji_od, formData.data_realizacji_do]);

  function handleStatusChange(e) {
    const chosenStatus = e.target.value;
    if (chosenStatus === "W realizacji" && (!formData.pracownicy || formData.pracownicy.length === 0)) {
      alert("Przypisz przynajmniej jednego pracownika."); return;
    }
    if (chosenStatus === "Zrealizowane" && (!formData.opis_prac?.trim() || !formData.data_realizacji_od?.trim() || !formData.data_realizacji_do?.trim())) {
      alert("Wypełnij opis prac oraz daty realizacji."); return;
    }
    if (chosenStatus === "Rozliczone") {
      const fields = [formData.cena, formData.koszt_praca, formData.koszt_materialy, formData.koszt_urzadzenia, formData.koszt_dojazd];
      if (fields.some(v => parseFloat(v) === 0)) {
        if (!window.confirm("Niektóre koszty wynoszą 0. Czy na pewno rozliczyć?")) return;
      }
    }
    setFormData(prev => ({ ...prev, status: chosenStatus }));
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (["cena", "koszt_praca", "koszt_materialy", "koszt_urzadzenia", "koszt_dojazd"].includes(name)) {
      setFormData({ ...formData, [name]: value < 0 ? 0 : value });
    } else if (name === "rabat_specjalny") {
      setFormData({ ...formData, [name]: value < 0 ? 0 : value > 100 ? 100 : value });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleOdbiorcaPracChange = (e) => {
    setOdbiorcaPrac(e.target.value);
  }

  const handleAddUrzadzenie = (id) => {
    const urz = availableUrzadzenia.find(u => u.id_urzadzenie === id);
    if (urz && !selectedUrzadzenia.find(u => u.id_urzadzenie === id)) {
      setSelectedUrzadzenia([...selectedUrzadzenia, urz]);
    }
  };
  const handleRemoveUrzadzenie = (id) => setSelectedUrzadzenia(selectedUrzadzenia.filter(u => u.id_urzadzenie !== id));
  
  const handleAddPracownik = (id) => {
    if (!formData.pracownicy.includes(id)) {
      setFormData(prev => ({ ...prev, pracownicy: [...prev.pracownicy, id] }));
    }
  };
  const handleRemovePracownik = (id) => setFormData(prev => ({ ...prev, pracownicy: prev.pracownicy.filter(p => p !== id) }));

  async function saveCostsForOrder(id_zlec) {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    const rowsToPersist = costRows.filter(r => r.id_towar && isQtyValid(r.ilosc) && r.nazwa_at_sale);
    
    const invalid = costRows.filter(r => (r.nazwa_at_sale?.trim() && !r.id_towar) || (r.id_towar && !isQtyValid(r.ilosc)));
    if (invalid.length) throw new Error("Popraw błędy w kosztach.");

    const currentIds = new Set(rowsToPersist.filter(r => r.id_koszt).map(r => r.id_koszt));
    const toDelete = (initialCostIdsRef.current || []).filter(id => !currentIds.has(id));

    const buildPayload = (row) => ({
      id_zlecenie: id_zlec, nazwa_at_sale: row.nazwa_at_sale, ilosc: toNum(row.ilosc),
      koszt_jednostkowy_netto_at_sale: toNum(row.koszt_jednostkowy_netto_at_sale),
      in_sprzedaz: !!row.in_sprzedaz, id_towar: row.id_towar, kategoria_id: row.kategoria_id,
      jednostka_id: row.jednostka_id, vat_proc_at_sale: row.vat_proc_at_sale, vat_id: row.vat_id,
      marza_at_sale: row.marza_at_sale ? toNum(row.marza_at_sale) : null,
      jednostka_symbol_at_sale: row.jednostka_symbol_at_sale, kategoria_nazwa_at_sale: row.kategoria_nazwa_at_sale,
      vat_nazwa_at_sale: row.vat_nazwa_at_sale,
    });

    await Promise.all([
      ...rowsToPersist.filter(r => !r.id_koszt).map(r => axios.post(`${API_URL}/koszty-pozycje`, buildPayload(r), { headers })),
      ...rowsToPersist.filter(r => r.id_koszt).map(r => axios.put(`${API_URL}/koszty-pozycje/${r.id_koszt}`, buildPayload(r), { headers })),
      ...toDelete.map(id => axios.delete(`${API_URL}/koszty-pozycje/${id}`, { headers }))
    ]);
  }

  // --- STANDARDOWY ZAPIS FORMULARZA (ZAKTUALIZUJ) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const updatedData = {
        ...formData,
        planowana_data_realizacji: formData.planowana_data_realizacji ? new Date(formData.planowana_data_realizacji).toISOString() : null,
        urzadzenia: selectedUrzadzenia.map(u => u.id_urzadzenie),
        data_realizacji_od: formData.data_realizacji_od ? new Date(formData.data_realizacji_od).toISOString() : null,
        data_realizacji_do: formData.data_realizacji_do ? new Date(formData.data_realizacji_do).toISOString() : null,
      };
      // Zauważ, że `odbiorcaPrac` nie ma tu wpływu, nic nie jest przesyłane do backendu.
      await axios.put(`${API_URL}/zlecenia/${id_zlecenia}`, updatedData, { headers: { Authorization: `Bearer ${token}` } });
      await saveCostsForOrder(id_zlecenia);
      await handleUploadFiles(); 
      alert("Zaktualizowano zlecenie!");
      onZamknij();
    } catch (err) {
      alert(err.response?.data?.error || "Błąd zapisu.");
    }
  };


  /* ================== GENEROWANIE I POBIERANIE PDF ================== */
  const buildDocDefinition = (signatureDataUrl = null) => {
    const formatDatePL = (str) => str ? new Date(str).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' }) : '-';
    
    const przypisaniPracownicy = formData.pracownicy.map(id => {
      const p = pracownicy.find(x => x.id_pracownika === id);
      return p ? `${p.imie} ${p.nazwisko}` : `ID: ${id}`;
    });

    const opiekun = pracownicy.find(p => p.id_pracownika === parseInt(formData.opiekun_zlecenia)) || {};

    const costTableBody = [
      [
        { text: 'Nazwa', style: 'tableHeader' },
        { text: 'Ilość', style: 'tableHeader' },
        { text: 'J.m.', style: 'tableHeader' },
      ]
    ];
    
    costRows.forEach(row => {
      if (row.id_towar && row.in_sprzedaz) {
        costTableBody.push([
          row.nazwa_at_sale || '-',
          row.ilosc?.toString() || '0',
          row.jednostka_symbol_at_sale || 'szt.',
        ]);
      }
    });

    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [28, 32, 28, 48],
      content: [
        {
          columns: [
            { width: 110, image: 'logo', fit: [110, 44] },
            {
              width: '*',
              stack: [
                { text: 'PROTOKÓŁ WYKONANIA ZLECENIA', style: 'title', alignment: 'right' },
                { text: `Zlecenie #${orderData?.numer_zlecenia || '-'}`, style: 'subtitle', alignment: 'right' },
              ],
            },
          ],
          margin: [0, 0, 0, 12],
        },
        {
          canvas: [{ type: 'line', x1: 0, y1: 0, x2: 540, y2: 0, lineWidth: 1, lineColor: '#D9E3F0' }],
          margin: [0, 0, 0, 12],
        },
        {
          table: {
            widths: ['50%', '50%'],
            body: [[
              {
                stack: [
                  { text: 'Klient', style: 'boxTitle' },
                  { text: clientInfo ? `${clientInfo.imie} ${clientInfo.nazwisko}` : 'Brak danych klienta', style: 'boxMain' },
                  { text: clientInfo?.nr_telefonu || 'Brak numeru telefonu', style: 'boxSub' },
                  { text: clientInfo?.adres_email || 'Brak adresu e-mail', style: 'boxSub' },
                ],
              },
              {
                stack: [
                  { text: 'Adres realizacji', style: 'boxTitle' },
                  { text: formatAddressLine(adresInfo), style: 'boxMain' },
                  { text: adresInfo?.kod_pocztowy ? `Kod pocztowy: ${adresInfo.kod_pocztowy}` : '', style: 'boxSub' },
                  { text: adresInfo?.kraj || 'Polska', style: 'boxSub' },
                ],
              },
            ]],
          },
          layout: {
            hLineColor: () => '#E5EAF3',
            vLineColor: () => '#E5EAF3',
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            paddingLeft: () => 10,
            paddingRight: () => 10,
            paddingTop: () => 8,
            paddingBottom: () => 8,
          },
          margin: [0, 0, 0, 14],
        },
        { text: 'Szczegóły zlecenia', style: 'section' },
        {
          table: {
            widths: ['25%', '25%', '25%', '25%'],
            body: [
              [
                { text: 'Rodzaj', style: 'metaHead' },
                { text: 'Status', style: 'metaHead' },
                { text: 'Planowana realizacja', style: 'metaHead' },
              ],
              [
                { text: formData.rodzaj_zlecenia || '-', style: 'metaVal' },
                { text: formData.status || '-', style: 'metaVal' },
                { text: formatDatePL(formData.planowana_data_realizacji), style: 'metaVal' },
              ],
            ],
          },
          layout: {
            fillColor: (rowIndex) => rowIndex === 0 ? '#F3F7FD' : null,
            hLineColor: () => '#E0E7F2',
            vLineColor: () => '#E0E7F2',
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 7,
            paddingBottom: () => 7,
          },
          margin: [0, 0, 0, 12],
        },
        { text: 'Opis zgłoszenia', style: 'section' },
        { text: formData.opis || '-', style: 'paragraph' },
        { text: 'Opis wykonanych prac', style: 'section', margin: [0, 14, 0, 6] },
        { text: formData.opis_prac || '-', style: 'paragraph' },
        {
          text: `Okres realizacji: ${formatDatePL(formData.data_realizacji_od)} – ${formatDatePL(formData.data_realizacji_do)}`,
          style: 'muted',
          margin: [0, 6, 0, 0],
        },
        { text: 'Pracownicy realizujący zlecenie', style: 'section', margin: [0, 14, 0, 6] },
        {
          ul: przypisaniPracownicy.length > 0 ? przypisaniPracownicy : ['Brak przypisanych pracowników'],
          margin: [0, 0, 0, 8],
        },

        costTableBody.length > 1 ? { text: 'Użyte materiały / usługi', style: 'section', margin: [0, 12, 0, 6] } : {},
        costTableBody.length > 1 ? {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto'],
            body: costTableBody,
          },
          layout: {
            fillColor: (rowIndex) => rowIndex === 0 ? '#F3F7FD' : null,
            hLineColor: () => '#E0E7F2',
            vLineColor: () => '#E0E7F2',
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 6,
            paddingBottom: () => 6,
          },
        } : {},
      ],
      footer: (currentPage, pageCount) => ({
        columns: [
          { width: '*', text: 'G3 Service Spółka Cywilna • ul. Czechowa 26, 52-231 Wrocław • NIP: 894-304-45-35 • tel. +48 660 750 350 • biuro@g3service.pl', style: 'footer' },
          { width: 'auto', text: `Strona ${currentPage} z ${pageCount}`, alignment: 'right', style: 'footer' }
        ],
        margin: [28, 0]
      }),
      images: { logo: logoImg },
      styles: {
        title: { fontSize: 17, bold: true, color: '#0F2A4D' },
        subtitle: { fontSize: 10.5, color: '#526581', margin: [0, 2, 0, 0] },
        section: { fontSize: 11, bold: true, color: '#0F2A4D', margin: [0, 8, 0, 4] },
        paragraph: { fontSize: 10.5, lineHeight: 1.28, color: '#1F2937' },
        muted: { fontSize: 9.5, color: '#6B7280' },
        boxTitle: { fontSize: 9, color: '#526581', bold: true, margin: [0, 0, 0, 2] },
        boxMain: { fontSize: 11, color: '#0F172A', bold: true, margin: [0, 0, 0, 2] },
        boxSub: { fontSize: 9.5, color: '#475569' },
        metaHead: { fontSize: 9, color: '#334155', bold: true },
        metaVal: { fontSize: 10, color: '#0F172A' },
        footer: { fontSize: 7.5, color: '#6B7280', lineHeight: 1.1 },
        tableHeader: { bold: true, fontSize: 9.5, color: '#1E3A5F' },
      },
      defaultStyle: { font: 'Roboto' }
    };

    if (formData.rodzaj_zlecenia === "Montaż") {
    docDefinition.content.push( 
      { text: 'Potwierdzenie wykonania prac', style: 'section', margin: [0, 0, 0, 10]},
      { text: `Potwierdzam, że urządzenia zostały podłączone do instalacji elektrycznej zgodnie z moimi oczekiwaniami, instalacja chłodnicza została poprowadzona według moich wytycznych. Nie wnoszę żadnych zastrzeżeń co do sposobu montażu, przebiegu prac oraz finalnego efektu.`, margin: [0, 5, 0, 40]}
    )
    }
    if (signatureDataUrl) {
      
      docDefinition.content.push({ text: 'Podpis odbiorcy prac', style: 'section', margin: [0, 16, 0, 5] });
      docDefinition.content.push({ image: signatureDataUrl, width: 150 });
      // Wrzucamy imię i nazwisko osoby odbierającej
      if (odbiorcaPrac && odbiorcaPrac.trim() !== '') {
        docDefinition.content.push({ text: `Imię i nazwisko: ${odbiorcaPrac}`, style: 'muted', margin: [0, 5, 0, 0] });
      }
      // Dodatkowe metadane uwiarygadniające moment podpisu
      docDefinition.content.push({ 
        text: `Podpis złożono na urządzeniu: ${new Date().toLocaleString('pl-PL')}\nUżytkownik zlecający generowanie dokumentu: ${uzytkownik?.imie || ''} ${uzytkownik?.nazwisko || ''}`,
        fontSize: 8,
        color: '#6B7280',
        margin: [0, 10, 0, 0]
      });
    }

    return docDefinition;
  };

  const handlePrzejdzDoPodgladu = () => {
    // 1. Walidacja osoby odbierającej
    if (!odbiorcaPrac || odbiorcaPrac.trim() === "") {
      alert("Proszę wpisać imię i nazwisko osoby odbierającej prace przed wygenerowaniem PDF.");
      return;
    }

    setInitialCostRows(costRows);

    // 3. Generuje podgląd
    const docDef = buildDocDefinition(null);
    pdfMake.createPdf(docDef).getDataUrl((dataUrl) => {
      setPdfPreviewUrl(dataUrl);
      setStep('preview');
    });
  };

  const handlePobierzPDFZPodpisem = () => {
    if (sigCanvas.current.isEmpty()) {
      alert("Proszę złożyć podpis przed pobraniem dokumentu.");
      return;
    }
    
    const signatureData = sigCanvas.current.getCanvas().toDataURL('image/png');
    const finalDocDef = buildDocDefinition(signatureData);
    
    const plikNazwa = `Protokol_Podpisany_${orderData?.numer_zlecenia || id_zlecenia}.pdf`;
    pdfMake.createPdf(finalDocDef).download(plikNazwa);

    alert("Dokument został zapisany na Twoim urządzeniu. Możesz go teraz ręcznie dodać w sekcji 'Pliki' przed zatwierdzeniem formularza.");
    setStep('form');
  };

  if (loading) return <div className="modal"><div className="modal-content">Przetwarzanie...</div></div>;

  const opiekunInfo = pracownicy.find((p) => p.id_pracownika === parseInt(formData.opiekun_zlecenia));
  const opiekunInfoName = opiekunInfo ? `${opiekunInfo.imie} ${opiekunInfo.nazwisko}` : "Nie przypisano";

  /* ================== WIDOKI KROKÓW ================== */

  if (step === 'preview') {
    return (
      <div className="modal">
        <div className="modal-content" style={{ width: '80%', height: '85vh', display: 'flex', flexDirection: 'column' }}>
          <h3>Podgląd protokołu</h3>
          <iframe src={pdfPreviewUrl} style={{ width: '100%', flexGrow: 1, border: '1px solid #ccc', marginBottom: '20px' }} title="Podgląd PDF" />
          <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '10px' }}>
            <button type="button" className="cancel-btn" onClick={() => setStep('form')}>Wstecz do edycji</button>
            <button type="button" className="save-btn" onClick={() => setStep('signature')}>Przejdź do podpisu</button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'signature') {
    return (
      <div className="modal">
        <div className="modal-content" style={{ maxWidth: '600px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h3>Podpis odbiorcy prac</h3>
          <div style={{ border: '2px dashed #ccc', marginBottom: '20px', background: '#f9f9f9', width: '100%', display: 'flex', justifyContent: 'center' }}>
            <SignatureCanvas 
              ref={sigCanvas} 
              penColor="black" 
              canvasProps={{ width: 500, height: 200, className: 'sigCanvas' }} 
            />
          </div>
          <div className="form-actions" style={{ width: '100%', justifyContent: 'space-between' }}>
            <button type="button" className="cancel-btn" onClick={() => setStep('preview')}>Wstecz do podglądu</button>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="button" className="secondary-btn" onClick={() => sigCanvas.current.clear()}>Wyczyść</button>
              <button type="button" className="save-btn" onClick={handlePobierzPDFZPodpisem}>Zapisz i Pobierz PDF</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // GŁÓWNY WIDOK FORMULARZA
  return (
    <div className="modal">
      <div className="modal-content">
        <button className="close-button" onClick={onZamknij}>X</button>
        <h3 className="order-edit-title">Edytuj zlecenie {orderData?.numer_zlecenia ? `#${orderData.numer_zlecenia}` : ""}</h3>

        <div className="client-address-container">
          {clientInfo && (
            <div className="client-info">
              <h4>{clientInfo.imie} {clientInfo.nazwisko}</h4>
              <p>{clientInfo.nr_telefonu || "Brak numeru telefonu"}</p>
              <p>{clientInfo.adres_email || "Brak adresu e-mail"}</p>
            </div>
          )}
          {adresInfo && (
            <div className="address-info">
              <h4>Adres realizacji</h4>
              <p>{formatAddressLine(adresInfo)}</p>
            </div>
          )}
        </div>

        <p className="opiekun-info">
          Opiekun zlecenia: {opiekunInfoName}
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-field">
              <label>Rodzaj zlecenia:</label>
              {canEditAllOrders
              ?<select name="rodzaj_zlecenia" value={formData.rodzaj_zlecenia} onChange={handleChange} disabled={isFieldDisabled("rodzaj_zlecenia", formData.status)}>
                <option>Serwis</option><option>Przegląd</option><option>Montaż</option><option>Audyt</option><option>Wizja</option><option>Sprzedaż</option><option>Inne</option>
              </select>
              :<p>{formData.rodzaj_zlecenia}</p>
              }
            </div>

            <div className="form-field">
              <label>Status:</label>
              {canEditAllOrders
              ?<select name="status" value={formData.status} onChange={handleStatusChange} disabled={isFieldDisabled("status", formData.status)}>
                <option value="Nowe" disabled={formData.status === "Zrealizowane" || formData.status === "Rozliczone"}>Nowe</option>
                <option value="W realizacji" disabled={formData.pracownicy?.length === 0 || formData.status === "Rozliczone"}>W realizacji</option>
                <option value="Zrealizowane" disabled={!canBeZrealizowane(formData)}>Zrealizowane</option>
                <option value="Rozliczone" disabled={!canBeZrealizowane(formData)}>Rozliczone</option>
                <option value="Anulowane">Anulowane</option>
              </select>
              :<select name="status" value={formData.status} onChange={handleStatusChange} disabled={isFieldDisabled("status", formData.status)}>
                <option value="Nowe" disabled>Nowe</option>
                <option value="W realizacji" disabled={formData.pracownicy?.length === 0 || formData.status === "Rozliczone"}>W realizacji</option>
                <option value="Zrealizowane" disabled={!canBeZrealizowane(formData)}>Zrealizowane</option>
                <option value="Rozliczone" disabled={!canBeZrealizowane(formData)}>Rozliczone</option>
                <option value="Anulowane">Anulowane</option>
              </select>
              }
            </div>

            <div className="form-field">
              <label>Planowana data:</label>
              {canEditAllOrders
              ?<input type="datetime-local" name="planowana_data_realizacji" value={formData.planowana_data_realizacji} onChange={handleChange} disabled={isFieldDisabled("planowana_data_realizacji", formData.status)} />
              :<input type="datetime-local" name="planowana_data_realizacji" value={formData.planowana_data_realizacji} disabled/>
              }
            </div>
          </div>

          <div className="form-grid">
            <div className="form-field">
              <label>Przypisz pracowników:</label>
              <select onChange={(e) => handleAddPracownik(parseInt(e.target.value))} disabled={isFieldDisabled("pracownicy", formData.status)}>
                <option value="">Wybierz pracownika</option>
                {pracownicy.map(p => <option key={p.id_pracownika} value={p.id_pracownika}>{p.imie} {p.nazwisko}</option>)}
              </select>
              <div className="selected-items-tags">
                {formData.pracownicy.map(id => {
                  const p = pracownicy.find(x => x.id_pracownika === id);
                  return (
                    <span key={id} className="tag">
                      {p ? `${p.imie} ${p.nazwisko}` : id}
                      <button type="button" onClick={() => handleRemovePracownik(id)} disabled={isFieldDisabled("pracownicy", formData.status)}>×</button>
                    </span>
                  );
                })}
              </div>
            </div>

            <div className="form-field full-width">
              <label>Opis zgłoszenia:</label>
              {canEditAllOrders
              ?<textarea name="opis" value={formData.opis} onChange={handleChange} disabled={isFieldDisabled("opis", formData.status)} style={{height: '80px'}} />
              :<p>{formData.opis}</p>
              }
            </div>
          </div>

          <div className="table-container">
            <KosztySprzedazTabela towary={towary} slowniki={slowniki} onChange={setCostRows} initialRows={initialCostRows} uzytkownik={uzytkownik}/>
          </div>

          <div className="form-grid mt-20">
            <div className="form-field">
              <label>Realizacja od:</label>
              <input 
                type="datetime-local" 
                name="data_realizacji_od" 
                value={formData.data_realizacji_od} 
                onChange={handleChange} 
                max={formData.data_realizacji_do} // Nie można wybrać daty późniejszej niż "do"
                disabled={isFieldDisabled("data_realizacji_od", formData.status)} 
              />

              <label className="mt-10">Realizacja do:</label>
              <input 
                type="datetime-local" 
                name="data_realizacji_do" 
                value={formData.data_realizacji_do} 
                onChange={handleChange} 
                min={formData.data_realizacji_od} // Nie można wybrać daty wcześniejszej niż "od"
                disabled={isFieldDisabled("data_realizacji_do", formData.status)} 
              />
            </div>
            
            <div className="form-field">
              <label>Opis wykonanych prac:</label>
              <textarea name="opis_prac" value={formData.opis_prac} onChange={handleChange} disabled={isFieldDisabled("opis_prac", formData.status)} style={{height: '115px'}} />
            </div>

            <div className="form-field">
              <label>Dodaj urządzenie:</label>
              <select onChange={(e) => handleAddUrzadzenie(parseInt(e.target.value))} disabled={isFieldDisabled("urzadzenia", formData.status)}>
                <option value="">Wybierz urządzenie</option>
                {availableUrzadzenia.map(u => <option key={u.id_urzadzenie} value={u.id_urzadzenie}>{u.nazwa} - {u.model} - {u.numer_seryjny}</option>)}
              </select>
              <div className="selected-items-tags">
                {selectedUrzadzenia.map(u => (
                  <span key={u.id_urzadzenie} className="tag">
                    {u.nazwa + " - " + u.model + " - "+ u.numer_seryjny} <button type="button" onClick={() => handleRemoveUrzadzenie(u.id_urzadzenie)} disabled={isFieldDisabled("urzadzenia", formData.status)}>×</button>
                  </span>
                ))}
              </div>
            </div>
          </div>
          
          {/* NOWE POLE - Odbiorca Prac */}
          <div className="form-grid mt-20">
             <div className="form-field">
               <label>Osoba odbierająca prace (tylko na protokół):</label>
               <input 
                 type="text" 
                 value={odbiorcaPrac} 
                 onChange={handleOdbiorcaPracChange} 
                 placeholder="Imię i nazwisko osoby do podpisu - minimum 3 znaki" 
               />
             </div>
          </div>

          <div className="order-files-section">
            <h4>Pliki ({existingOrderFiles.length + orderFiles.length})</h4>
            <div className="file-upload-controls">
              <input type="file" multiple ref={fileInputRef} style={{ display: "none" }} onChange={handleFilesChange} />
              <button type="button" className="secondary-btn" onClick={handleChooseFilesClick}>Dodaj pliki</button>
            </div>

            <div className="thumbnails-grid">
              {orderFiles.map((file, idx) => (
                <div key={idx} className="thumb new-file">
                  <span className="file-label">NOWY</span>
                  <button type="button" className="remove-btn" onClick={() => handleRemoveFile(idx)}>×</button>
                </div>
              ))}
              {existingOrderFiles.map((f) => (
                <div key={f.id_pliku} className="thumb">
                  {f.typ_pliku.startsWith("image/") ? (
                    <img src={previewMap[f.id_pliku]} alt="podgląd" onClick={() => handleDownloadExistingFile(f.id_pliku)} />
                  ) : <div className="file-icon" onClick={() => handleDownloadExistingFile(f.id_pliku)}>DOC</div>}
                  <button type="button" className="remove-btn" onClick={() => handleDeleteExistingFile(f.id_pliku)}>🗑️</button>
                </div>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onZamknij}>Wstecz</button>
            <div className="main-btns" style={{ display: 'flex', gap: '10px' }}>
              {odbiorcaPrac.length > 3 && formData.status === "Zrealizowane"
              ?<button type="button" className="secondary-btn" onClick={handlePrzejdzDoPodgladu}>
                Generuj PDF z podpisem
              </button>
              :<button type="button" disabled style={{background: "gray"}} className="secondary-btn" onClick={handlePrzejdzDoPodgladu}>
                Generuj PDF z podpisem
              </button>
              }
              {formData.status === "Zrealizowane" && canContinueOrder
                ?<button type="button" className="continuation-btn" onClick={() => onKontynuacja(orderData)}>Kontynuacja</button>
                :<></>
              }
              <button type="submit" className="save-btn">Zaktualizuj</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FormularzEdycjiZlecenia;